<template>
  <w-sliding-popup ref="detailPopup" type="full" title="트랜잭션 상세 정보" @close="onClosePopup">
    <div slot="content" v-loading="popupLoading">
      <el-row class="mr-b10" type="flex" justify="end">
        <el-button size="mini" @click="showSearchTool = !showSearchTool"><i class="fas fa-search" /></el-button>
      </el-row>

      <el-row class="mr-b25" type="flex" justify="space-between">
        <el-col :span="7">
          <highcharts ref="scatterChart" :options="options" />
        </el-col>
        <el-col :span="17">
          <el-table-wrapper v-loading="loading" style="width: 100%" ref="txTable" max-height="250" row-class-name="c-pointer" :data="keyList"
            :columns="columns" :column-default="columnDefault" border :pagination="true" :show-custom-header="true"
            :show-search-tool="showSearchTool" highlight-current-row @current-change="onChangeSelection">
            <template slot-scope="scope" slot="state-slot">
              <i v-if="scope.row.error !== 0" class="fas fa-circle text-mini state-abnormal" />
              <i v-else class="fas fa-circle text-mini state-normal" />
            </template>
          </el-table-wrapper>
        </el-col>
      </el-row>

      <el-tabs class="tab-button" v-model="activeTab" v-loading="tabLoading">
        <el-tab-pane label="Call 리스트" name="callList">
          <w-call-list-table ref="callList" :activeTab="activeTab" :params="txParams" />
        </el-tab-pane>
        <el-tab-pane label="Call 트리" name="calltree">
          <w-call-tree-view ref="callTree" :activeTab="activeTab" :params="txParams" />
        </el-tab-pane>
        <el-tab-pane v-if="existInfo.existError === 'Y'" label="에러" name="error">
          <w-error-view ref="errorTable" :activeTab="activeTab" :params="txParams" />
        </el-tab-pane>
        <el-tab-pane v-if="existInfo.existEumNav === 'Y'" label="NAV" name="eumNav">
          <w-nav-view ref="navView" :activeTab="activeTab" :params="eumParams" />
        </el-tab-pane>
        <el-tab-pane v-if="existInfo.existEumAjax === 'Y'" label="AJAX" name="eumAjax">
          <w-ajax-table ref="ajaxTable" :activeTab="activeTab" :params="eumParams" />
        </el-tab-pane>
        <el-tab-pane v-if="existInfo.existEumJsError === 'Y'" label="JS Errorr" name="eumJsError">
          <w-js-error-table ref="jserrorTable" :activeTab="activeTab" :params="eumParams" />
        </el-tab-pane>
      </el-tabs>
    </div>
  </w-sliding-popup>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, map, filter, isEmpty, upperFirst, concat } from 'lodash';
// import find from 'lodash/find';
import { columnDefault } from '@/constants/table';
import { TX_ID_STRING_LIST, TX_ID_STRING, OBJ_HASH, START_DATETIME, END_DATETIME } from '@/constants/apm-domain-define';
import { convertValuByUnit, convertMillisecondsToSeconds } from '@/utils/number';
import { getDateToString } from '@/utils/date';
import { EMPTY_TEXT } from '@/utils/string';
import TransactionAPI from '@/api/apm/transaction';
import WCallListTable from '../table/CallListTable';
import WCallTreeView from '../CallTreeView';
import WErrorView from '../ErrorView';
import WJsErrorTable from '../table/JsErrorTable';
import WAjaxTable from '../table/AjaxTable';
import WNavView from '../NavView';

const radius = 2;
const scatterOptions = {
  chart: {
    type: 'scatter',
    backgroundColor: 'transparent',
    zoomType: 'xy',
    height: 250,
  },
  boost: {
    enabled: true,
    /**
    * GPU Translation은 JavaScript Translation보다 빠르나, 특정 data set에서 렌더링 문제를 일으킬 수 있다.
    * 부동소수점 정밀도 때문에 data set에 timestamp와 같이 조금씩 증가하는 숫자가 많으면 제대로 작동하지 않는다.
    * 주석 처리해도 default가 false라 상관없지만, 명확히 하기 위해 false로 설정한다.
    * @author Seung
    **/
    useGPUTranslations: false,
    usePreallocated: true,
    seriesThreshold: 1,
  },
  title: false,
  credits: false,
  xAxis: {
    type: 'datetime',
    labels: {
      style: {
        color: '$luckygray',
        fontSize: '9px',
      },
    },
  },
  yAxis: {
    title: false,
    minTickInterval: 1,
    startOnTick: false,
    labels: {
      style: {
        color: '$luckygray',
        fontSize: '9px',
      },
    },
  },
  plotOptions: {
    series: {
      animation: false,
      allowPointSelect: true,
      stickyTracking: false,
      marker: {
        radius,
        // states: {
        //   select: {
        //     enabled: false,
        //     radius: 5,
        //   },
        // },
      },
    },
  },
  legend: false,
  series: [{
    data: [],
  }],
};

export default {
  name: 'WTransactionDetailPopup',
  components: {
    WCallListTable,
    WCallTreeView,
    WErrorView,
    WJsErrorTable,
    WAjaxTable,
    WNavView,
  },
  props: {
    scatterData: Object,
    txType: String,
  },
  data() {
    return {
      options: cloneDeep(scatterOptions),
      keyList: [],
      columnDefault,
      columns: [
        { label: '상태', width: 50, headerAlign: 'center', scopedSlot: 'state-slot' },
        { prop: 'serviceText', label: '서비스', width: 500, headerAlign: 'center', searchable: true, sortable: true },
        { prop: 'statusCode', label: '응답 코드', width: 100, headerAlign: 'center', searchable: true, formatter: this.convertStatus },
        {
          label: '처리 시간',
          headerAlign: 'center',
          childColumns: [
            { prop: 'elapsed', label: '경과', width: 80, headerAlign: 'center', sortable: true, formatter: this.convertMillisecondsToSeconds },
            { prop: 'cpu', label: 'CPU', width: 80, headerAlign: 'center', sortable: true, formatter: this.convertMillisecondsToSeconds },
            { prop: 'sqlTime', label: 'SQL', width: 80, headerAlign: 'center', sortable: true, formatter: this.convertMillisecondsToSeconds },
            { prop: 'apicallTime', label: 'API 호출', width: 80, headerAlign: 'center', sortable: true, formatter: this.convertMillisecondsToSeconds },
          ],
        },
        { prop: 'kbytes', label: '메모리 (byte)', width: 100, headerAlign: 'center', sortable: true, formatter: this.convertValuByByte },
        { prop: 'endTime', label: '종료시각', minWidth: 180, headerAlign: 'center', searchable: true, sortable: true, searchType: 'date', formatter: this.convertDateToString },
      ],
      highlightRecord: {},
      activeTab: 'callList',
      showSearchTool: false,
      loading: false,
      colorSetB: [],
      colorSetR: [],
      txParams: {},
      eumParams: {},
      existInfo: {
        existError: 'N',
        existEumAjax: 'N',
        existEumJsError: 'N',
        existEumNav: 'N',
      },
      popupLoading: false,
      tabLoading: false,
    };
  },
  computed: {
    ...mapGetters(['isRelativeAnalysisDate', 'selectedNode']),
  },
  watch: {
    /**
     * 트랜잭션 테이블 데이터 로딩때마다 sactter 차트 상태 리셋
     * @return {[type]} [description]
     * @author jda
     */
    keyList() {
      this.resetHighlightPoint();
    },
  },
  methods: {
    /**
     * 상세정보 팝업창 open, scatter 데이터 로드
     * @return {[type]} [description]
     * @author jda
     */
    open() {
      this.$refs.detailPopup.visible = true;
      this.setChartEvent();

      this.popupLoading = true;
      this.$nextTick(() => {
        TransactionAPI.getTxScatterList(this.scatterData).then((response) => {
          if (response.data.success) {
            const result = response.data.data;
            if (!isEmpty(result)) {
              // unit으로 y축 및 툴팁 설정
              this.setChartAttributeByUnit(result.unitY);
              result.body[0].type = 'scatter';
              result.body[0].turboThreshold = 0;
              result.body[0].boostThreshold = 1;

              this.options.series = result.body;
            } else {
              this.options.series[0].data = [];
            }
          } else {
            this.options.series[0].data = [];
          }
        }).finally(() => {
          this.popupLoading = false;
        });
      });
    },
    onClosePopup() {
      // 첫번째 탭으로 이동.
      this.activeTab = 'callList';
      this.showSearchTool = false;

      // 데이터 초기화
      this.options = cloneDeep(scatterOptions);
      this.keyList = [];
      this.highlightRecord = {};
      this.txParams = {};
      this.eumParmas = {};

      // 각 탭 리셋
      this.resetTransactionTab();

      // 팝업 닫음
      this.$refs.detailPopup.visible = false;
    },
    /**
     * 트랜잭션 상세 테이블 리셋
     * @author jda
     */
    resetTransactionTab() {
      const refs = this.$refs;
      refs.callList.reset();
      refs.callTree.reset();
    },
    /**
     * 선택된 데이터(point) 리셋
     * @author jda
     */
    resetHighlightPoint() {
      const highlightRecord = this.highlightRecord;
      if (!isEmpty(highlightRecord)) {
        highlightRecord.setState();
        highlightRecord.update({
          marker: {
            radius,
          },
        });
      }
    },
    /**
     * 차트 이벤트 설정
     * @author jda
     */
    setChartEvent() {
      const me = this;
      Object.assign(this.options.chart, {
        events: {
          selection(e) {
            const series = me.options.series;
            if (!isEmpty(series)) {
              let txIds = [];
              let datetimeSet = [];
              series.forEach((scatterSeries) => {
                const includedData = filter(scatterSeries.data, (data) => data.x >= e.xAxis[0].min && data.x <= e.xAxis[0].max && data.y >= e.yAxis[0].min && data.y <= e.yAxis[0].max);
                txIds = concat(txIds, map(includedData, 'txid'));
                datetimeSet = concat(datetimeSet, map(includedData, 'x'));
              });

              /**
              * 드래그한 영역 내의 startDatetime, endDatetime을 requestParam에 담아 검색 구간을 좁혀 속도를 향상시킨다
              * @author Seung
              */
              let startDatetime = '';
              let endDatetime = '';

              if(!isEmpty(datetimeSet)) {
                startDatetime = datetimeSet[0];
                endDatetime = datetimeSet[0];
                datetimeSet.forEach((datetime) => {
                  if(datetime < startDatetime) {
                    startDatetime = datetime;
                  } else if(datetime > endDatetime) {
                    endDatetime = datetime;
                  }
                });
              }

              const requestParam = {};
              requestParam[TX_ID_STRING_LIST] = txIds;
              requestParam[OBJ_HASH] = me.selectedNode.objHash;
              requestParam[START_DATETIME] = startDatetime;
              requestParam[END_DATETIME] = endDatetime;
              me.loadTransactionRawList(requestParam);
            }
            return false;
          },
        },
      });

      // x축 포맷 설정
      Object.assign(this.options.xAxis.labels, {
        format: this.popupPath === 'analysis' && !this.isRelativeAnalysisDate ? '{value: %Y-%m-%d <br /> %H:%M}' : '{value: %H:%M}',
      });

      // 포인터 선택 이벤트
      Object.assign(this.options.plotOptions.series, {
        point: {
          events: {
            select() {
              const requestParam = {};
              requestParam[TX_ID_STRING_LIST] = [this.txIds];
              requestParam[OBJ_HASH] = null;
              me.loadTransactionRawList(requestParam);

              me.resetHighlightPoint();
              me.highlightRecord = this;
              this.setState('hover');
            },
          },
        },
      });
    },
    /**
     * 드래그 또는 선택한 트랜잭션의 raw data 로드
     * @param  {[type]} txid [description]
     * @return {[type]}      [description]
     * @author jda
     */
    loadTransactionRawList(requestParam) {
      const txIds = requestParam[TX_ID_STRING_LIST];

      if(txIds !== null && !isEmpty(txIds)) {
        this.loading = true;
        this.$refs.txTable.clearFilter();
        TransactionAPI.getTxRawList(requestParam).then((response) => {
          this.loading = false;
          if (response.data.success) {
            this.keyList = response.data.data;
          } else {
            this.keyList = [];
          }
        });
      } else {
        this.keyList = [];
      }
    },
    /**
     * 차트 y축 label 및 툴팁 설정
     * @param  {[type]} unit [description]
     * @author jda
     */
    setChartAttributeByUnit(unit) {
      // yAxis label 설정
      Object.assign(this.options.yAxis.labels, {
        formatter() {
          return unit === 'ms' ? convertMillisecondsToSeconds(this.value) : convertValuByUnit(this.value, unit, true, true);
        },
      });

      const txType = this.txType;
      // 툴팁
      this.options.tooltip = {
        formatter() {
          const point = this.point;
          const xDate = getDateToString(point.x);
          const yData = unit === 'ms' ? `${(point.y / 1000).toFixed(2)}s` : `${point.y}%`;
          return `<span style="color:${point.color}">\u25CF</span> ${xDate}<br /> ${txType}: <b>${yData}</b>`;
        },
      };
    },
    /**
     * [convertDateToString description]
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertDateToString(row, column, value) {
      return getDateToString(value, 'YYYY-MM-DD HH:mm:ss.SSS');
    },
    /**
     * ms -> s변환
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertMillisecondsToSeconds(row, column, value) {
      return convertMillisecondsToSeconds(value);
    },
    /**
     * KB의 데이터 변환
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertValuByByte(row, column, value) {
      if (value <= 0) {
        return 0;
      }
      return convertValuByUnit(value, 'KB', true, true, true);
    },
    /**
     * 선택된 row가 없을 경우 하위 탭 리셋,
     * 선택된 row의 txid, gxid를 이용하여 하위 탭 로드
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onChangeSelection(record) {
      if (isEmpty(record)) {
        this.resetTransactionTab();
      } else {
        this.tabLoading = true;
        // 트랜잭션 선택 후, Call Tree/Error/End User 데이터 로드
        // this.resetHighlightPoint();
        // const colorCode = record.error !== 0 ? 'R' : 'B';
        //
        // const chart = this.$refs.scatterChart.chart;
        // if (!isEmpty(chart.series)) {
        //   const series = find(chart.series, (data) => data.userOptions.colorCode === colorCode);
        //   const findRecord = find(series.data, { txid: record.txidString });
        //   console.log(series, findRecord);
        //   this.highlightRecord = findRecord;
        //   findRecord.setState('hover');
        //   findRecord.update({
        //     marker: {
        //       radius: 5,
        //     },
        //   });
        // }

        const txid = record.txidString;
        const requestParam = {};
        requestParam[TX_ID_STRING] = txid;
        requestParam[OBJ_HASH] = record.objHash;
        TransactionAPI.getTxRawDataExistStatus(requestParam).then((response) => {
          this.tabLoading = false;
          if (response.data.success) {
            const result = response.data.data;
            this.existInfo = result;
            this.changeTab(result);

            this.txParams = { txid };
            this.eumParams = { gxid: record.gxidString, objHash: record.objHash };
          }
        });
      }
    },
    /**
     * 현재 탭을 표현하지 않을 경우 첫번째 탭(콜 리스트)로 돌아간다.
     * @param  {[type]} existInfo [description]
     * @return {[type]}           [description]
     * @author jda
     */
    changeTab(existInfo) {
      const tabName = this.activeTab;
      if (tabName === 'callList' || tabName === 'calltree') {
        return;
      }
      const currentTab = upperFirst(tabName);
      if (existInfo[`exist${currentTab}`] === 'N') {
        this.activeTab = 'callList';
      }
    },
    /**
     * 응답 코드가 0일 경우 '-' 표현
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertStatus(row, column, value) {
      if (value === 0 || isEmpty(value)) {
        return EMPTY_TEXT;
      }
      return value;
    },
  },
};
</script>

<style>
</style>



// WEBPACK FOOTER //
// src/views/apm/transaction-matrix/components/popup/TransactionDetailPopup.vue